import { useCallback, useMemo } from 'react'

import { Controller } from 'react-hook-form'

import { XFrameEmbeddingStatus } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { getInvalidDomains } from './utils/embed.utils.js'

export type FormFields = {
  status: XFrameEmbeddingStatus
  allowedDomains: string
}

export const EmbedSettings = () => {
  const { $t } = useI18n()
  const { data: network, isLoading } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading: isUpdating } =
    useUpdateNetwork()
  const defaultValues: FormFields = useMemo(
    () => ({
      status:
        network?.settings?.xFrameEmbedding?.status ??
        XFrameEmbeddingStatus.enabledForAll,
      allowedDomains: (
        network?.settings?.xFrameEmbedding?.allowedDomains ?? []
      ).join(','),
    }),
    [
      network?.settings?.xFrameEmbedding?.allowedDomains,
      network?.settings?.xFrameEmbedding?.status,
    ],
  )

  const onSubmit = async (props: FormFields) => {
    const { status, allowedDomains } = props

    try {
      await updateNetwork({
        input: {
          settings: {
            xFrameEmbedding: {
              status,
              allowedDomains: allowedDomains
                .split(new RegExp(/[,;\s]/, 'g')) // split by comma, semicolon or whitespace (including newlines)
                .map(d => d.trim())
                .filter(d => d !== ''),
            },
          },
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Security settings updated.',
          id: 'Admin.Security.SettingsUpdated',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Unable to update security settings.',
          id: 'Admin.Security.FailedToUpdateSettings',
        }),
        status: 'error',
      })
    }
  }

  const validateDomains = useCallback(
    (value: string) => {
      const invalidDomains = getInvalidDomains(value)

      if (!value) {
        return $t({
          defaultMessage: 'Please enter at least one domain',
          id: 'Admin.Security.EmbedSettings.AllowedDomains.Validation.EmptyError',
        })
      }

      if (invalidDomains.length > 0) {
        return $t(
          {
            defaultMessage:
              'Invalid domains. Please make sure you have entered valid domains separated by commas. Following domains are invalid: {invalidDomains}',
            id: 'Admin.Security.EmbedSettings.AllowedDomains.ValidationError',
          },
          {
            invalidDomains: invalidDomains.join(', '),
          },
        )
      }

      return true
    },
    [$t],
  )

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Embedding Control',
          id: 'Admin.Security.EmbedSettings',
        })}
        description={$t({
          defaultMessage:
            'Allow or prevent your site from being embedded in external websites or applications. When disabled, embedding will not be allowed, enhancing security but restricting the ability to embed your site.',
          id: 'Admin.Security.EmbedSettings.Description',
        })}
      />
      <Card.Content>
        <Form<FormFields> defaultValues={defaultValues} onSubmit={onSubmit}>
          {({ formState: { isDirty }, control }) => {
            return (
              <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.RadioGroup
                        {...field}
                        value={field.value}
                        onChange={field.onChange}
                      >
                        <RadioGroup.Items>
                          <RadioGroup.Item
                            value={XFrameEmbeddingStatus.enabledForAll}
                            title={$t({
                              defaultMessage: 'Allow embedding in all domains',
                              id: 'Admin.Security.EmbedSettings.Allow',
                            })}
                            description={$t({
                              defaultMessage:
                                'Your site can be embedded in any external websites or applications.',
                              id: 'Admin.Security.EmbedSettings.Allow.Description',
                            })}
                          />
                          <RadioGroup.Item
                            value={
                              XFrameEmbeddingStatus.enabledForSpecificDomains
                            }
                            title={$t({
                              defaultMessage:
                                'Allow embedding in specific domains',
                              id: 'Admin.Security.EmbedSettings.AllowSpecific',
                            })}
                            description={$t({
                              defaultMessage:
                                'Specify the domains where embedding is allowed',
                              id: 'Admin.Security.EmbedSettings.AllowSpecific.Description',
                            })}
                            extraContent={
                              field.value ===
                              XFrameEmbeddingStatus.enabledForSpecificDomains ? (
                                <Form.Textarea
                                  name="allowedDomains"
                                  placeholder={$t({
                                    defaultMessage:
                                      'e.g. https://example.com, https://*.yourdomain.com',
                                    id: 'Admin.Security.EmbedSettings.AllowedDomains.Placeholder',
                                  })}
                                  helperText={
                                    <T
                                      defaultMessage="You can enter multiple domains separated by commas."
                                      id="Admin.Security.EmbedSettings.AllowedDomains.HelperText"
                                      values={{
                                        protocols: chunks => (
                                          <span className="font-bold">
                                            {chunks}
                                          </span>
                                        ),
                                        example: chunks => (
                                          <span className="font-bold">
                                            {chunks}
                                          </span>
                                        ),
                                        subdomains: chunks => (
                                          <span className="font-bold">
                                            {chunks}
                                          </span>
                                        ),
                                      }}
                                    />
                                  }
                                  tertiaryLabel={$t({
                                    defaultMessage:
                                      'Please make sure to use valid domains',
                                    id: 'Admin.Security.EmbedSettings.AllowedDomains.TertiaryLabel',
                                  })}
                                  validation={{
                                    validate:
                                      field.value ===
                                      XFrameEmbeddingStatus.enabledForSpecificDomains
                                        ? validateDomains
                                        : undefined,
                                  }}
                                  onKeyDown={e => {
                                    // Prevent arrow key events from bubbling up to the RadioGroup
                                    if (
                                      e.key === 'ArrowUp' ||
                                      e.key === 'ArrowDown' ||
                                      e.key === 'ArrowLeft' ||
                                      e.key === 'ArrowRight' ||
                                      e.key === 'Enter'
                                    ) {
                                      e.stopPropagation()
                                    }
                                  }}
                                />
                              ) : null
                            }
                          />
                          <RadioGroup.Item
                            value={XFrameEmbeddingStatus.disabled}
                            title={$t({
                              defaultMessage: 'Disallow embedding',
                              id: 'Admin.Security.EmbedSettings.Disallow',
                            })}
                            description={$t({
                              defaultMessage:
                                'Embedding will be disabled for all domains, providing an additional layer of security.',
                              id: 'Admin.Security.EmbedSettings.Disallow.Description',
                            })}
                          />
                        </RadioGroup.Items>
                      </Form.RadioGroup>
                      {field.value === XFrameEmbeddingStatus.disabled && (
                        <Alert status="info">
                          <T
                            defaultMessage="Disabling embedding will block all IFrame embedding attempts, which could affect external integrations using IFrames."
                            id="Admin.Security.EmbedSettings.Disallow.Warning"
                          />
                        </Alert>
                      )}
                    </>
                  )}
                />
                <Form.Actions>
                  <Button
                    type="submit"
                    loading={isLoading || isUpdating}
                    disabled={!isDirty}
                  >
                    <T defaultMessage="Update" id="Generics.Update" />
                  </Button>
                </Form.Actions>
              </div>
            )
          }}
        </Form>
      </Card.Content>
    </Card>
  )
}
